/**
 * /email-log — Audit trail de envíos de email.
 *
 * Tabla paginada con filtros: tax_client, status, date range.
 * Cada fila tiene "Re-enviar" (POST /resend → 501 hasta D.4).
 */

import { useMemo, useState } from 'react'
import { CheckCircle2, ChevronLeft, ChevronRight, Clock, Loader2, Mail, RotateCw, XCircle } from 'lucide-react'

import { ClientSelector } from '../pdf-templates/components/client-selector'
import { useTaxClients } from '@/hooks/api/use-tax-clients'
import { useEmailLog, useResendEmail } from '@/hooks/api/use-email-log'
import { DOC_TYPE_LABELS, type EmailLog, type EmailLogFilter } from '@/lib/api-email-templates'

const PAGE_SIZE = 50

export function EmailLogPage() {
  const [taxClientId, setTaxClientId] = useState<string>('')
  const [status, setStatus] = useState<EmailLogFilter['status']>('')
  const [page, setPage] = useState(0)

  const filter: EmailLogFilter = useMemo(
    () => ({
      taxClientId: taxClientId || undefined,
      status: status || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [taxClientId, status, page],
  )

  const clients = useTaxClients()
  const log = useEmailLog(filter)
  const resend = useResendEmail()

  const totalPages = log.data ? Math.ceil(log.data.total / PAGE_SIZE) : 0

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-wider text-fg-muted">
          ▸ Auditoría
        </p>
        <h1 className="mt-1 text-3xl font-bold text-fg-primary">Email Log</h1>
        <p className="mt-2 max-w-2xl text-sm text-fg-secondary">
          Audit trail de cada envío de email (notificación de documento DIAN).
          Retención 5 años (regla DIAN). Para fallos, "Re-enviar" reintenta
          el envío.
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-border bg-bg-surface p-4 shadow-sm">
        <ClientSelector
          value={taxClientId}
          onChange={(v) => {
            setTaxClientId(v)
            setPage(0)
          }}
          clients={clients.data?.items ?? []}
          loading={clients.isLoading}
        />
        <div className="flex items-center gap-2">
          <label htmlFor="status-filter" className="text-sm font-medium text-fg-secondary">
            Status:
          </label>
          <select
            id="status-filter"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as EmailLogFilter['status'])
              setPage(0)
            }}
            className="rounded-md border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary"
          >
            <option value="">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="sent">Enviado</option>
            <option value="failed">Fallido</option>
            <option value="bounced">Bounced</option>
          </select>
        </div>
        {log.data && (
          <div className="ml-auto flex items-center gap-2 text-sm text-fg-muted">
            {/* placeholderData mantiene las filas viejas mientras llega la
                página nueva — este indicador sutil muestra que se actualiza. */}
            {log.isFetching && (
              <span className="inline-flex items-center gap-1 text-xs">
                <Loader2 className="h-3 w-3 animate-spin" />
                Actualizando…
              </span>
            )}
            {log.data.total.toLocaleString('es-CO')} resultados
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="overflow-hidden rounded-xl border border-border bg-bg-surface shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-elevated">
              <tr>
                <Th>Status</Th>
                <Th>Fecha</Th>
                <Th>Documento</Th>
                <Th>Tipo</Th>
                <Th>Destinatario</Th>
                <Th>Subject</Th>
                <Th className="text-right">Intentos</Th>
                <Th className="text-right">Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {log.isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-fg-muted">
                    Cargando…
                  </td>
                </tr>
              )}
              {!log.isLoading && log.data?.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-fg-muted">
                    <Mail className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    Sin envíos para los filtros aplicados.
                  </td>
                </tr>
              )}
              {log.data?.items.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  onResend={() => resend.mutate(row.id)}
                  resending={resend.isPending && resend.variables === row.id}
                />
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {log.data && totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <p className="text-xs text-fg-muted">
              Página {page + 1} de {totalPages}
            </p>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || log.isFetching}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ChevronLeft className="h-3 w-3" />
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={page + 1 >= totalPages || log.isFetching}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
              >
                Siguiente
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-3 text-left font-semibold text-fg-secondary ${className}`}>
      {children}
    </th>
  )
}

function Row({
  row,
  onResend,
  resending,
}: {
  row: EmailLog
  onResend: () => void
  resending: boolean
}) {
  return (
    <tr className="border-t border-border">
      <td className="px-4 py-3">
        <StatusBadge status={row.status} />
      </td>
      <td className="px-4 py-3 text-fg-secondary">
        {new Date(row.queued_at).toLocaleString('es-CO', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
        })}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-fg-primary">{row.doc_number}</td>
      <td className="px-4 py-3 text-fg-secondary">{DOC_TYPE_LABELS[row.doc_type]}</td>
      <td className="px-4 py-3 text-fg-secondary">{row.recipient_address}</td>
      <td className="max-w-xs px-4 py-3 text-fg-secondary">
        <div className="truncate" title={row.subject}>{row.subject}</div>
      </td>
      <td className="px-4 py-3 text-right text-fg-secondary">{row.attempt_count}</td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={onResend}
          disabled={resending || row.status === 'sent'}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
          title={RESEND_TITLES[row.status]}
        >
          <RotateCw className={`h-3 w-3 ${resending ? 'animate-spin' : ''}`} />
          Re-enviar
        </button>
      </td>
    </tr>
  )
}

/** Tooltip del botón Re-enviar según el estado del envío. */
const RESEND_TITLES: Record<EmailLog['status'], string> = {
  sent: 'Ya fue enviado correctamente — no requiere re-envío',
  pending: 'El envío está en cola — re-enviar lo encola de nuevo',
  failed: 'El envío falló — re-enviar lo reintenta',
  bounced: 'El destinatario rebotó el email — verifica la dirección antes de re-enviar',
}

function StatusBadge({ status }: { status: EmailLog['status'] }) {
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
        <CheckCircle2 className="h-3 w-3" />
        Enviado
      </span>
    )
  }
  if (status === 'failed' || status === 'bounced') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
        <XCircle className="h-3 w-3" />
        {status === 'bounced' ? 'Bounced' : 'Fallido'}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-700">
      <Clock className="h-3 w-3" />
      Pendiente
    </span>
  )
}
